import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, type LinkProps, useSearch } from '@tanstack/react-router'

import type { MyOrder } from '@/bindings/MyOrder'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { sendFeedback } from '@/features/feedback/api'
import { FeedbackButton, SentNote } from '@/features/feedback/form'
import { myOrdersKeys, myOrdersQuery, orderTrackingPath } from '@/features/ordering/api'
import { whereLabel } from '@/features/ordering/order-rules'
import { ExpiryCountdown, StatusBadge } from '@/features/ordering/order-view'
import { minorUnits } from '@/lib/currencies'
import { cx } from '@/lib/cx'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatPrice, formatQuantity } from '@/lib/menu-format'
import { useClampedPage } from '@/lib/paging'
import { MY_ORDERS_PATH } from '@/lib/return-path'
import { formatWhen } from '@/lib/time'

/**
 * `/app/my-orders` (H6): the account's orders across every restaurant —
 * the active ones first, then the settled ones paged. Each row opens the
 * tracking page (G7), where cancel lives. Anonymous orders never show; the
 * phone that placed them keeps their codes (G6).
 */
export function MyOrdersPage() {
  const t = useT()
  const { page } = useSearch({ strict: false }) as { page: number }
  const link = (page: number): LinkProps => ({ to: MY_ORDERS_PATH, search: { page } })
  const list = useQuery(myOrdersQuery(page))
  useClampedPage(page, list.data && { items: list.data.past, total: list.data.total }, link)

  return (
    <Page width="md" title={t('myOrders.title')} className="flex flex-col gap-6">
      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && !list.data && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data && list.data.active.length === 0 && list.data.total === 0 && (
        <Card>
          <p className="text-muted text-sm">{t('myOrders.empty')}</p>
        </Card>
      )}
      {list.data && list.data.active.length > 0 && <Section title={t('myOrders.active')} orders={list.data.active} />}
      {list.data && list.data.total > 0 && (
        <>
          <Section title={t('myOrders.past')} orders={list.data.past} />
          <Pager label={t('myOrders.pager')} page={page} total={list.data.total} link={link} />
        </>
      )}
    </Page>
  )
}

function Section({ title, orders }: { title: string; orders: MyOrder[] }) {
  return (
    <section className="flex flex-col gap-2" aria-label={title}>
      <h2 className="text-lg font-semibold">{title}</h2>
      <ul className="flex flex-col gap-2">
        {orders.map((order) => (
          <li key={`${order.venue.branch_slug}/${order.code}`}>
            <Row order={order} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function Row({ order }: { order: MyOrder }) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(order.venue.restaurant_main_language)
  const decimals = minorUnits(order.currency)
  const seating = whereLabel(t, order.table_name)
  const summary = order.lines.map((line) => `${formatQuantity(line.quantity, language)} × ${name(line.name)}`).join(', ')
  const body = (
    <>
      <p className="text-muted truncate text-sm">
        {order.venue.restaurant_name} · {order.venue.branch_name}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">{t('track.title', { number: order.number })}</span>
        <StatusBadge status={order.status} />
      </div>
      <div className="text-muted flex flex-wrap items-center justify-between gap-2 text-sm">
        <span>
          {seating} · {formatWhen(order.created_at, language)}
        </span>
        <span className="text-fg font-semibold tabular-nums">{formatPrice(order.total, decimals, language)}</span>
      </div>
      {summary && <p className="line-clamp-2 text-sm">{summary}</p>}
      {order.placed_by_staff && <p className="text-muted text-sm">{t('myOrders.atCounter')}</p>}
      <ExpiryCountdown order={order} />
      {!order.venue.active && <p className="text-muted text-sm">{t('myOrders.venueGone')}</p>}
    </>
  )
  const className = 'flex flex-col gap-1'
  return (
    <div className="flex flex-col gap-2">
      {order.venue.active ? (
        <Link
          to={orderTrackingPath(order.venue.restaurant_slug, order.venue.branch_slug, order.code)}
          className={cx(className, 'bg-surface border-border hover:bg-accent-soft block rounded-xl border p-4')}
        >
          {body}
        </Link>
      ) : (
        <Card className={className}>{body}</Card>
      )}
      <RowFeedback order={order} />
    </div>
  )
}

/** The sent text (kept even once the venue is gone), or a button that opens the form in place (I3). */
function RowFeedback({ order }: { order: MyOrder }) {
  const t = useT()
  const queryClient = useQueryClient()
  const label = t('feedback.order.title')
  if (order.feedback) {
    return (
      <div className="px-4 text-sm" aria-label={label}>
        <SentNote feedback={order.feedback} />
      </div>
    )
  }
  if (!order.venue.active) return null
  return (
    <FeedbackButton
      label={label}
      variant="ghost"
      size="sm"
      send={(message) => sendFeedback({ target: 'restaurant', restaurant_id: null, branch_id: null, order_id: order.id, message, path: null })}
      onSent={() => void queryClient.invalidateQueries({ queryKey: myOrdersKeys.all })}
    />
  )
}
